import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

interface AppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** 하단 액션 영역. */
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children?: React.ReactNode;
  overlayClassName?: string;
  onCloseAutoFocus?: ComponentProps<typeof DialogContent>['onCloseAutoFocus'];
}

const SIZE = { sm: 'sm:max-w-md', md: 'sm:max-w-lg', lg: 'sm:max-w-3xl' } as const;

/**
 * 모달 공통 래퍼 — shadcn Dialog 위에 제목·설명·본문·푸터 슬롯을 고정. AModal 대체.
 * 폼 모달은 children 에 <form> 을 두고 footer 에 submit 버튼(form 속성)으로 연결한다.
 */
export function AppDialog({
  open,
  onOpenChange,
  title,
  description,
  footer,
  size = 'md',
  className,
  children,
  overlayClassName,
  onCloseAutoFocus,
}: AppDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(SIZE[size], className)}
        overlayClassName={overlayClassName}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              {typeof title === 'string' ? title : '대화상자'}
            </DialogDescription>
          )}
        </DialogHeader>
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
